import { createRootRoute, createRoute, createRouter, Outlet } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { PatternsDemo } from './patterns/demo'
import { LoginScreen, SignupScreen } from './screens/auth'
import { BudgetScreen } from './screens/budget'
import { CeremonyScreen } from './screens/ceremony'
import { GuestSiteScreen } from './screens/guest-site'
import { GuestsScreen } from './screens/guests'
import { OverviewScreen } from './screens/overview'
import { PhotoScreen } from './screens/photo'
import { ReceptionScreen } from './screens/reception'
import { ScheduleScreen } from './screens/schedule'
import { CatalogScreen, SuppliersScreen } from './screens/suppliers'
import { TasksScreen } from './screens/tasks'
import { VenuesScreen } from './screens/venues'
import { AppLayout } from './shell/app-layout'
import { ComingSoon } from './shell/coming-soon'
import { ScenarioPanel } from './shell/scenario-panel'

// Code-based routes on purpose: screens live in src/screens/<area>/ and the
// router stays one readable file. Links are untyped strings (no Register).

const root = createRootRoute({
  component: () => (
    <>
      <Outlet />
      <ScenarioPanel />
    </>
  ),
  notFoundComponent: () => (
    <main className="grid min-h-dvh place-items-center p-8 text-center">
      <div className="space-y-2">
        <p className="text-xs font-semibold tracking-[0.14em] text-action">404</p>
        <h1 className="font-heading text-3xl">Tahle stránka neexistuje</h1>
      </div>
    </main>
  ),
})

const login = createRoute({ getParentRoute: () => root, path: '/login', component: LoginScreen })
const signup = createRoute({ getParentRoute: () => root, path: '/signup', component: SignupScreen })
const guestSite = createRoute({
  getParentRoute: () => root,
  path: '/svatba',
  component: GuestSiteScreen,
})
const guestReply = createRoute({
  getParentRoute: () => root,
  path: '/svatba/$partyId',
  component: GuestSiteScreen,
})

const planner = createRoute({
  getParentRoute: () => root,
  id: 'planner',
  component: () => (
    <AppLayout>
      <Outlet />
    </AppLayout>
  ),
})

const page = (path: string, component: () => ReactNode) =>
  createRoute({ getParentRoute: () => planner, path, component })

const soon = createRoute({
  getParentRoute: () => planner,
  path: '/soon/$slug',
  component: function Soon() {
    const { slug } = soon.useParams()
    return <ComingSoon slug={slug} />
  },
})

const routeTree = root.addChildren([
  login,
  signup,
  guestSite,
  guestReply,
  planner.addChildren([
    page('/', OverviewScreen),
    page('/tasks', TasksScreen),
    page('/tasks/$taskId', TasksScreen),
    page('/guests', GuestsScreen),
    page('/guests/replies', GuestsScreen),
    page('/guests/replies/$replyId', GuestsScreen),
    page('/guests/settings', GuestsScreen),
    page('/guests/$partyId', GuestsScreen),
    page('/suppliers', SuppliersScreen),
    page('/suppliers/catalog', CatalogScreen),
    page('/suppliers/catalog/$catalogId', CatalogScreen),
    page('/suppliers/$supplierId', SuppliersScreen),
    page('/schedule', ScheduleScreen),
    page('/schedule/$eventId', ScheduleScreen),
    page('/budget', BudgetScreen),
    page('/budget/$expenseId', BudgetScreen),
    page('/venues', VenuesScreen),
    page('/venues/new', VenuesScreen),
    page('/venues/$venueId', VenuesScreen),
    page('/soon/misto', VenuesScreen),
    page('/ceremony', CeremonyScreen),
    page('/ceremony/details', CeremonyScreen),
    page('/ceremony/scenario', CeremonyScreen),
    page('/ceremony/scenario/$stepId', CeremonyScreen),
    page('/ceremony/people', CeremonyScreen),
    page('/ceremony/people/officiant', CeremonyScreen),
    page('/ceremony/people/witnesses', CeremonyScreen),
    page('/ceremony/people/roles/$roleId', CeremonyScreen),
    page('/ceremony/place', CeremonyScreen),
    page('/ceremony/place/details', CeremonyScreen),
    page('/ceremony/place/plan-b', CeremonyScreen),
    page('/ceremony/place/preparations/$preparationId', CeremonyScreen),
    page('/ceremony/materials', CeremonyScreen),
    page('/ceremony/materials/notes', CeremonyScreen),
    page('/ceremony/materials/summary', CeremonyScreen),
    page('/ceremony/materials/files/$documentId', CeremonyScreen),
    page('/photo', PhotoScreen),
    page('/photo/crew/$crewId', PhotoScreen),
    page('/photo/groups', PhotoScreen),
    page('/photo/brief', PhotoScreen),
    page('/reception', ReceptionScreen),
    page('/soon/jidlo-a-piti', ReceptionScreen),
    page('/patterns', PatternsDemo),
    soon,
  ]),
])

export const router = createRouter({ routeTree, defaultPreload: 'intent' })
